import React, { useState } from 'react';
import { Brain, Check, ChevronRight } from 'lucide-react';
import T from 'Components/Helpers/translate';

type Props = {
	// Accumulated reasoning text (the parent merges the streamed snapshots).
	thinking: string;
	isStreaming: boolean;
	// Optional controlled expand state. When provided, the parent owns it (used for
	// the live streaming block). Otherwise the block owns its own state, seeded once
	// from `defaultExpanded` — this lets a finished block keep the expand choice made
	// while it was streaming without following later resets of the live state.
	expanded?: boolean;
	onToggle?: (next: boolean) => void;
	defaultExpanded?: boolean;
	// Pages only (WEB-14623): while streaming, show the latest line of the
	// reasoning beside the label; once done, say how long it took. Without them
	// the block reads "Thinking…" / "Thought", as in the Assistant chat.
	preview?: boolean;
	durationMs?: number;
};

// "8 s" / "2 min 41 s". Never below a second: a duration that rounds to zero
// would read as a measurement while carrying none.
export function formatThinkingDuration(ms: number): string {
	const total = Math.max(1, Math.round(ms / 1000));
	if (total < 60) return T('assistant.pages.activity.durationSeconds', { seconds: total });
	return T('assistant.pages.activity.durationMinutes', { minutes: Math.floor(total / 60), seconds: total % 60 });
}

// The last non-empty line of the reasoning so far — what it is thinking about now.
// Scans back from the end: the text can be tens of KB and re-renders on every chunk.
function latestLine(thinking: string): string {
	let end = thinking.length;
	while (end > 0) {
		const start = thinking.lastIndexOf('\n', end - 1);
		const line = thinking.slice(start + 1, end).trim();
		if (line) return line;
		end = start;
	}
	return '';
}

const ThinkingBlock = ({
	thinking,
	isStreaming,
	expanded: expandedProp,
	onToggle,
	defaultExpanded,
	preview,
	durationMs
}: Props) => {
	const [internalExpanded, setInternalExpanded] = useState(defaultExpanded ?? false);
	const expanded = expandedProp ?? internalExpanded;
	const toggle = () => {
		const next = !expanded;
		onToggle?.(next);
		if (expandedProp === undefined) setInternalExpanded(next);
	};

	return (
		<div className="pr-think">
			<button type="button" className="pr-think-summary" onClick={toggle} aria-expanded={expanded}>
				<Brain
					style={{ width: 14, height: 14 }}
					className={isStreaming ? 'pr-think-summary-icon pr-think-pulse' : 'pr-think-summary-icon'}
				/>
				<span className="pr-think-summary-text">
					{isStreaming
						? T('assistant.thinking')
						: durationMs !== undefined
						? T('assistant.thoughtFor', { duration: formatThinkingDuration(durationMs) })
						: T('assistant.thought')}
				</span>
				{isStreaming && preview && !expanded ? (
					<span className="pr-think-preview">{latestLine(thinking)}</span>
				) : null}
				<ChevronRight
					style={{ width: 14, height: 14 }}
					className={expanded ? 'pr-think-chev pr-think-chev-open' : 'pr-think-chev'}
				/>
			</button>
			{expanded && (
				<div className="pr-think-detail">
					<div className="pr-think-full">{thinking}</div>
					{!isStreaming && (
						<div className="pr-think-done">
							<Check style={{ width: 13, height: 13 }} />
							<span>{T('assistant.thinkingDone')}</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default ThinkingBlock;
